import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, nullableText } from './common.js';
import { PermissionCodeSchema } from './permissions.js';
import { PublicStageSchema, ShipmentTypeSchema } from './enums.js';
/**
 * Status kodlari — avtomatlashtirish kalitlari, hech qachon o'zgarmaydi.
 *
 * `labelUz` / `labelRu` / `publicStage` / `notify` admin panelda sozlanadi,
 * `code` esa kodga bog'langan: `CONFIRMED` da qoldiq rezervlanadi,
 * `CANCELLED` da bo'shatiladi.
 *
 * O'tish grafi BU YERDA EMAS — u `status_transitions` jadvalida
 * (avtopart-status-machine skill). Bu yerda faqat kodlar ro'yxati.
 */
export const ORDER_STATUS_CODES = [
    // umumiy boshlanish
    'NEW',
    'CONFIRMED',
    'AWAITING_PAYMENT',
    'PAID',
    // SHOP oqimi
    'PACKED',
    // CHINA oqimi
    'SUPPLIER_ORDERED',
    'SUPPLIER_SHIPPED',
    'IN_TRANSIT',
    'ARRIVED_BORDER',
    'CUSTOMS_DECLARED',
    'CUSTOMS_CLEARED',
    'AT_WAREHOUSE',
    'READY',
    // umumiy yakun
    'OUT_FOR_DELIVERY',
    'DELIVERED',
    'COMPLETED',
    // istisnolar
    'ON_HOLD',
    'PARTIALLY_SHIPPED',
    'FAILED_DELIVERY',
    'RETURN_REQUESTED',
    'RETURNED',
    'CANCELLED',
];
export const OrderStatusCodeSchema = z.enum(ORDER_STATUS_CODES);
/** Admin panelda status kartochkasi. */
export const OrderStatusResponseSchema = z.object({
    code: OrderStatusCodeSchema,
    labelUz: z.string(),
    labelRu: nullableText(80),
    publicStage: PublicStageSchema,
    flow: ShipmentTypeSchema.nullable(),
    position: z.number().int(),
    isTerminal: z.boolean(),
    isException: z.boolean(),
    notify: z.boolean(),
    notifyTemplate: nullableText(64),
    colorHex: nullableText(9),
});
/** Admin faqat shu maydonlarni sozlay oladi — `code` va graf sozlanmaydi. */
export const UpdateOrderStatusConfigSchema = z
    .object({
    labelUz: z.string().trim().min(1).max(80).optional(),
    labelRu: z.string().trim().max(80).nullable().optional(),
    publicStage: PublicStageSchema.optional(),
    notify: z.boolean().optional(),
    /*
     * BO'SH SATR yaramaydi — `null` yoki haqiqiy nom.
     *
     * `.min(1)` siz `""` o'tib ketardi va u `null` EMAS: push
     * yaratish sharti (`notifyTemplate !== null`) o'tib, shablon
     * nomi bo'sh bo'lgani uchun xabar keyingi bosqichda yiqilardi.
     * Ya'ni "sozlamada push bor, mijozga hech narsa ketmaydi"
     * holati boshqa eshikdan qaytib kelardi.
     */
    notifyTemplate: z.string().trim().min(1).max(64).nullable().optional(),
    colorHex: z.string().trim().max(9).nullable().optional(),
})
    .strict();
/** Ruxsat etilgan o'tish — jadvaldan o'qiladi, kodda hisoblanmaydi. */
export const StatusTransitionResponseSchema = z.object({
    fromCode: OrderStatusCodeSchema,
    toCode: OrderStatusCodeSchema,
    /**
     * Maqsad statusning yorlig'i.
     *
     * Admin UI tugmani SHU MATN bilan chizadi. Kod (`toCode`) —
     * avtomatlashtirish kaliti, u ekranga chiqarilmaydi. Yorliq admin
     * panelda sozlanadi, shuning uchun klientda takrorlanmaydi.
     */
    toLabelUz: z.string(),
    toLabelRu: nullableText(80),
    /**
     * Shu statusga o'tilganda mijozga push KETADIMI.
     *
     * Maqsad statusning `notify` bayrog'i. Operator uchun bu QAROR
     * ma'lumoti: push ketmaydigan statusdan keyin mijozga o'zi
     * qo'ng'iroq qilishi kerakligini biladi. Usiz operator mijoz
     * xabardor deb o'ylab o'tib ketardi.
     */
    toNotify: z.boolean(),
    requiredPermission: PermissionCodeSchema.nullable(),
    requiresComment: z.boolean(),
    requiresEta: z.boolean(),
});
/**
 * Statusni o'zgartirish so'rovi.
 *
 * `comment` va `newEta` majburiyligi o'tishning o'ziga bog'liq
 * (`requiresComment` / `requiresEta`) — buni server jadvaldan tekshiradi,
 * schema darajasida majburlab bo'lmaydi.
 */
export const ChangeShipmentStatusSchema = z
    .object({
    toCode: OrderStatusCodeSchema,
    comment: z.string().trim().max(500).optional(),
    newEtaFrom: IsoDateTimeSchema.optional(),
    newEtaTo: IsoDateTimeSchema.optional(),
})
    .strict()
    .refine((v) => v.newEtaFrom === undefined || v.newEtaTo === undefined || v.newEtaFrom <= v.newEtaTo, {
    message: "ETA boshlanishi tugashidan keyin bo'lishi mumkin emas",
    path: ['newEtaTo'],
});
/** Jo'natma tarixi — har o'zgarish yoziladi, bo'sh qolmaydi. */
export const ShipmentStatusHistoryResponseSchema = z.object({
    id: IdSchema,
    fromCode: OrderStatusCodeSchema.nullable(),
    toCode: OrderStatusCodeSchema,
    /**
     * Yorliqlar — ekranda SHULAR ko'rsatiladi.
     *
     * Kod avtomatlashtirish kaliti; uni tarixda ham chiqarish tugmalarda
     * yorliq ishlatish qoidasiga zid bo'lardi. `fromLabelUz` `null` —
     * jo'natmaning birinchi yozuvi (undan oldin status bo'lmagan).
     */
    fromLabelUz: z.string().nullable(),
    toLabelUz: z.string(),
    actorId: IdSchema.nullable(),
    actorName: z.string().nullable(),
    comment: nullableText(500),
    newEtaFrom: IsoDateTimeSchema.nullable(),
    newEtaTo: IsoDateTimeSchema.nullable(),
    notifiedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
});
/** Terminal statuslar — ulardan oldinga o'tish yo'q. */
export const TERMINAL_STATUS_CODES = ['COMPLETED', 'RETURNED', 'CANCELLED'];
/** Bu o'tishlarda izoh majburiy bo'lishi kutiladi (seed shuni ta'minlaydi). */
export const COMMENT_REQUIRED_TARGETS = ['CANCELLED', 'RETURNED', 'RETURN_REQUESTED'];
//# sourceMappingURL=statuses.js.map